<template>
  <help-dialog title="Domain Model" maxWidth="900px">
    <template #default>
      <h3 class="mb-2 text-black">
        <v-icon>mdi-alpha-d-box</v-icon>&nbsp;Domain Model Overview
      </h3>
      <p class="text-grey-darken-3">
        The Domain Model is a visual representation of domain concepts and the
        relationships between them. The Domain Model is a key artifact in Domain-Driven
        Design (DDD) and is used to create a shared understanding of the domain between
        stakeholders and developers. Powerful use cases include:
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3">API design</li>
        <li class="text-grey-darken-3">System modernization or microservice design</li>
        <li class="text-grey-darken-3">Breaking down monolithic systems</li>
        <li class="text-grey-darken-3">Code generation</li>
      </ul>

      <br/><br/>
      <!-- Commands are grouped by aggregate. You can also see the
      command parameters and the associated entities. You can further group entities in Bounded
      Contexts and even generate the code code skeleton for a Bounded Context (a Service API). -->

      <h3 class="mb-2 text-black">
        <v-icon icon="mdi-cog"></v-icon>&nbsp;Configuration
      </h3>
      <p class="text-grey-darken-3">
        First, make sure to have a card type set as ‘Command’
        and another set as ‘Aggregate Root’ in workflow settings (the
        <v-icon size="x-small">mdi-cog</v-icon> icon top right).
      </p>

      <br/>

      <img src="../../public/help-aggregates-1.png" alt="help-aggregates-1" style="max-width: 100%;"/>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Add cards to Events
      </h3>
      <p class="text-grey-darken-3">
        Now make sure to add one <b>Command</b> name and one <b>Aggregate Root</b> name to each event in the diagram
        by using cards of the correct type.
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3"><b>Note:</b> Add only add <b>one Command</b> and <b>one Root Entitu</b> per Event. Additional cards
          will be ignored.
        </li>
        <li class="text-grey-darken-3">
          <b>Hint:</b> When modeling, consider each event as the result of one command executed on one main entity (Aggregate root) and somtimes also on related entities.
          If the event, <b>Created order</b> can be accomplished in two steps,
          such as <b>1. Created customer</b> and <b>2. Created Order</b>, then break up the event into two steps.
        </li>
      </ul>
      <br/>
      <br/>

      <img src="../../public/help-aggregates-2.png" alt="help-aggregates-2" style="max-width: 100%;"/>

      <br/><br/>

      <h3 class="mb-2 text-black">
        View Root Entities
      </h3>
      <p class="text-grey-darken-3">
        Now you can view Commands and Events grouped by its Aggregate Root.
        The names and descriptions can be updated directly in the diagram.
        In the example below we see that the Aggregate Root <b>Lead</b> has 3 commands:
      </p>
      <br>
      <ul class="ml-5">
        <li class="text-grey-darken-3">Create lead</li>
        <li class="text-grey-darken-3">Qualify lead</li>
        <li class="text-grey-darken-3">Approve lead</li>
      </ul>

      <br/>

      <p class="text-grey-darken-3">
        Commands and events belong together if they are positioned
        on the same level horizontally. The first row can be read as:
        <i>“The actor <b>Sales Representative</b> invokes the command <b>Create Lead</b> on the entity <b>Lead</b>
          and by doing so triggers the event: <b>Created lead</b>”</i>
      </p>

      <br/>

      <img src="../../public/help-aggregates-3.png" alt="help-aggregates-3" style="max-width: 100%;"/>
      <br><br>

      <h3 class="mb-2 text-black">
        Data Fields
      </h3>
      <p class="text-grey-darken-3">
        If you have defined Commands along with Entities under the tab Read Model & Command you
        can also view the fields and related entities. Or hide them by deselecting the checkboxes
        <b>Show data fields</b> and <b>Show entities</b>.
      </p>

      <br/>

      <img class="mb-24" src="../../public/help-aggregates-4.png" alt="help-aggregates-4" style="max-width: 100%;"/>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Bounded Contexts
      </h3>
      <p class="text-grey-darken-3">
        The next step is to assign one or several <b>Entities</b> to one or many <b>Bounded contexts</b>. This is done by clicking
        the button <b>Select bounded context</b> on the entity. Related entities can belong to the same bounded
        context as the Aggregate Root or to another bounded context. If a related entity belongs to another bounded
        context, a label with the text <b>Integration with other bounded contexts</b> indicates that we have
        an integration point.
      </p>

      <br/>
      <img class="mb-24" src="../../public/help-aggregates-5.png" alt="help-aggregates-6" style="max-width: 100%;"/>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Service API / Code Generation
      </h3>
      <p class="text-grey-darken-3">
        Code can be generated for a <b>Bounded Context</b>, such as a full Service API or an
        OpenAPI/Swagger Specification. You can select from various programming languages,
        databases, and testing frameworks. Additionally, there is an option to generate
        only the OpenAPI Specification. If you wish to use a different technology stack,
        you can manually update the prompt (as a YAML specification) used for AI code generation by
        clicking on 'Customize specification.' For example, you could choose to generate code in
        TypeScript instead of JavaScript. After finalizing the specification, click the
        'Generate API' button. Wait for the generation to complete, then download the source
        code as a ZIP file.
      </p>
      <br/>
      <img class="mb-24" src="../../public/help-aggregates-6.png" alt="help-aggregates-7" style="max-width: 100%;"/>
    </template>
  </help-dialog>
</template>

<script>
import HelpDialog from '@/components/HelpDialog.vue'

export default {
  components: {
    HelpDialog
  }
}
</script>
